import { useClientPortalAPI } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Order, SelectedBundlesType } from "./OrderContext";

interface Props {
  order: Order;
  setOrder: (order: SetStateAction<Order>) => void;
  isQuickscreen: boolean;
  isEnabled: boolean;
  onBundlesRemoved?: (remainingBundles: SelectedBundlesType[]) => void;
}

/**
 * Drops selected bundles that the order's group no longer offers, e.g. bundles
 * restored from a saved draft or kept after switching groups. Standard package
 * orders submit the package's own bundles, so they are not checked here;
 * quickscreen always submits `order.bundles`, even if `packageData` lingers.
 */
export function useValidateOrderBundles({
  order,
  setOrder,
  isQuickscreen,
  isEnabled,
  onBundlesRemoved,
}: Props) {
  const { t } = useTranslation();
  const submitsPackageBundles = !!order.packageData && !isQuickscreen;
  const shouldValidate =
    isEnabled && order.bundles.length > 0 && !submitsPackageBundles;
  const { data: availableBundles, error } = useClientPortalAPI(
    shouldValidate && order.group
      ? `/api/internal/client-portal/customer-groups/${order.group}/available-bundles/`
      : null,
    { is_quickscreen: isQuickscreen }
  );
  const isLoadingBundles =
    shouldValidate && !!order.group && !availableBundles && !error;

  const availableBundleIds = useMemo(
    () => new Set((availableBundles ?? []).map(({ id }) => id)),
    [availableBundles]
  );
  const remainingBundles = useMemo(
    () => order.bundles.filter(({ id }) => availableBundleIds.has(id)),
    [availableBundleIds, order.bundles]
  );
  const hasUnavailableBundles =
    shouldValidate &&
    !isLoadingBundles &&
    remainingBundles.length !== order.bundles.length;

  useEffect(() => {
    if (!hasUnavailableBundles) {
      return;
    }

    setOrder((prev) => ({
      ...prev,
      bundles: prev.bundles.filter(({ id }) => availableBundleIds.has(id)),
    }));
    onBundlesRemoved?.(remainingBundles);
    if (!availableBundles) {
      showToast(
        t(
          "We couldn't confirm your bundled deals, so they were removed from your order. Please add them again."
        ),
        { type: "error" }
      );
    } else {
      showToast(
        t(
          "Some bundled deals are no longer available for this group and were removed from your order"
        ),
        { type: "info" }
      );
    }
  }, [
    availableBundleIds,
    availableBundles,
    hasUnavailableBundles,
    onBundlesRemoved,
    remainingBundles,
    setOrder,
    t,
  ]);

  return {
    isValidatingBundles: isLoadingBundles || hasUnavailableBundles,
    isLoadingBundles,
    isRemovingAllItems:
      hasUnavailableBundles && !remainingBundles.length && !order.checks.length,
  };
}
